// Client-side video trimming (WhatsApp-style): extract only the selected window
// from a (possibly very large) source video and re-encode it into a small clip,
// so we upload just the chosen segment instead of the whole multi-hundred-MB file.
//
// Pipeline (Chromium / Android WebView):
//   video.captureStream() -> video track
//   Web Audio (createMediaElementSource -> MediaStreamDestination) -> audio track
//   MediaRecorder(combined, bitrate-capped) -> small WebM clip
// Audio is routed through a GainNode(0) so the speakers stay silent while the
// AudioContext still receives PCM (muting the element would drop the audio track).
//
// Lot 616 — iPhone / iPad (WKWebView, Safari) : pas de captureStream sur <video> → les images sont dessinées
// dans un <canvas> (requestVideoFrameCallback / rAF) et c'est canvas.captureStream() qui alimente le
// MediaRecorder (sortie MP4 H.264/AAC côté Safari). Si le son ne peut pas être capturé (AudioContext bloqué),
// on abandonne (erreur) : l'appelant se rabat sur la découpe serveur, qui garde le son.

const canCaptureElement = () =>
  typeof HTMLMediaElement !== "undefined" && typeof HTMLMediaElement.prototype.captureStream === "function";
const canCaptureCanvas = () =>
  typeof HTMLCanvasElement !== "undefined" && typeof HTMLCanvasElement.prototype.captureStream === "function";

export const canTrimVideo = ({ allowCanvas = true } = {}) =>
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined" &&
  (canCaptureElement() || (allowCanvas && canCaptureCanvas()));

// Vrai quand la découpe locale passera par le canvas (iPhone) — exposé pour les tests / diagnostics.
export const trimUsesCanvas = ({ allowCanvas = true } = {}) => !canCaptureElement() && allowCanvas && canCaptureCanvas();

// Grab a single still frame (JPEG data URL) from a video file at `time` seconds —
// used to preview the exact start of the chosen extract before publishing.
export const captureVideoFrame = (file, time = 0, maxW = 320) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    const done = (val, err) => {
      video.removeAttribute("src");
      try { video.load(); } catch { /* noop */ }
      URL.revokeObjectURL(url);
      err ? reject(err) : resolve(val);
    };
    video.onloadedmetadata = () => {
      const t = Math.max(0, Math.min(time, (video.duration || time) - 0.05));
      const onSeeked = () => {
        try {
          const scale = Math.min(1, maxW / (video.videoWidth || maxW));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round((video.videoWidth || maxW) * scale);
          canvas.height = Math.round((video.videoHeight || maxW) * scale);
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          done(canvas.toDataURL("image/jpeg", 0.7));
        } catch (e) { done(null, e); }
      };
      video.addEventListener("seeked", onSeeked, { once: true });
      try { video.currentTime = t; } catch { done(null, new Error("seek-failed")); }
    };
    video.onerror = () => done(null, new Error("frame-load-error"));
    setTimeout(() => done(null, new Error("frame-timeout")), 12000);
  });

const pickMimeType = () => {
  const cands = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const c of cands) {
    try { if (window.MediaRecorder.isTypeSupported(c)) return c; } catch { /* noop */ }
  }
  return "video/webm";
};

const seekTo = (video, t) =>
  new Promise((resolve) => {
    const on = () => { video.removeEventListener("seeked", on); resolve(); };
    video.addEventListener("seeked", on);
    try { video.currentTime = t; } catch { resolve(); }
    setTimeout(resolve, 2500); // safety: never hang on a stuck seek
  });

/**
 * @param {File} file source video
 * @param {number} startSec  window start (seconds)
 * @param {number} durationSec  window length (seconds)
 * @param {{ videoBitrate?: number, audioBitrate?: number, allowCanvas?: boolean, onProgress?: (0..1)=>void }} opts
 * @returns {Promise<File>} small trimmed clip (WebM/MP4)
 */
export const trimVideoToClip = async (file, startSec, durationSec, opts = {}) => {
  const useCanvas = trimUsesCanvas({ allowCanvas: opts.allowCanvas !== false });
  if (!canTrimVideo({ allowCanvas: opts.allowCanvas !== false })) throw new Error("trim-unsupported");
  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.src = url;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.preload = "auto";
  // Keep it in the DOM (off-screen) so the WebView keeps decoding frames.
  video.style.cssText = "position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;";
  document.body.appendChild(video);

  let audioCtx = null;
  let dest = null;
  let raf = 0;
  let rvfc = 0;
  let canvas = null;

  const cleanup = () => {
    if (raf) cancelAnimationFrame(raf);
    if (rvfc && video.cancelVideoFrameCallback) { try { video.cancelVideoFrameCallback(rvfc); } catch { /* noop */ } }
    try { dest && dest.disconnect(); } catch { /* noop */ }
    try { audioCtx && audioCtx.close(); } catch { /* noop */ }
    try { video.pause(); } catch { /* noop */ }
    video.removeAttribute("src");
    try { video.load(); } catch { /* noop */ }
    if (video.parentNode) video.parentNode.removeChild(video);
    if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    URL.revokeObjectURL(url);
  };

  try {
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("video-load-error"));
      setTimeout(() => reject(new Error("video-load-timeout")), 20000);
    });

    const total = isFinite(video.duration) && video.duration > 0 ? video.duration : startSec + durationSec;
    const start = Math.max(0, Math.min(startSec, Math.max(0, total - 0.1)));
    const len = Math.max(0.5, Math.min(durationSec, total - start));

    await seekTo(video, start);

    // Audio graph (reliable capture even though speakers stay silent).
    let audioTrack = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        audioCtx = new AC();
        const srcNode = audioCtx.createMediaElementSource(video);
        dest = audioCtx.createMediaStreamDestination();
        const gain = audioCtx.createGain();
        gain.gain.value = 0;
        srcNode.connect(gain);
        gain.connect(audioCtx.destination);
        srcNode.connect(dest);
        audioTrack = dest.stream.getAudioTracks()[0] || null;
      }
    } catch { audioTrack = null; }

    let vt = null;
    let drawFrame = null;
    if (useCanvas) {
      // iPhone : dessin image par image dans un canvas (≤ 720 px de grand côté) capturé à 30 i/s.
      canvas = document.createElement("canvas");
      const vw = video.videoWidth || 720, vh = video.videoHeight || 1280;
      const scale = Math.min(1, 720 / Math.max(vw, vh));
      canvas.width = Math.max(2, Math.round(vw * scale / 2) * 2);
      canvas.height = Math.max(2, Math.round(vh * scale / 2) * 2);
      canvas.style.cssText = "position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;";
      document.body.appendChild(canvas);
      const ctx = canvas.getContext("2d");
      drawFrame = () => { try { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); } catch { /* noop */ } };
      drawFrame();
      vt = canvas.captureStream(30).getVideoTracks()[0] || null;
    } else {
      vt = video.captureStream().getVideoTracks()[0] || null;
    }
    const tracks = [];
    if (vt) tracks.push(vt);
    if (audioTrack) tracks.push(audioTrack);
    const combined = new MediaStream(tracks);

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(combined, {
      mimeType,
      videoBitsPerSecond: opts.videoBitrate || 2_500_000,
      audioBitsPerSecond: opts.audioBitrate || 128_000,
    });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

    video.muted = false; // let audio flow into the graph (gain 0 = silent output)
    if (audioCtx && audioCtx.state === "suspended") { try { await audioCtx.resume(); } catch { /* noop */ } }
    // Canvas (iPhone) : sans son capturable, on préfère la découpe serveur (qui garde le son) à un clip muet.
    if (useCanvas && audioTrack && audioCtx && audioCtx.state !== "running") throw new Error("audio-blocked");

    const stopped = new Promise((resolve) => { recorder.onstop = () => resolve(); });
    recorder.start(100);
    try { await video.play(); } catch (e) { if (useCanvas) throw new Error("play-blocked"); }
    if (useCanvas && video.paused) throw new Error("play-blocked");

    await new Promise((resolve) => {
      const tick = () => {
        if (drawFrame) drawFrame();
        const elapsed = video.currentTime - start;
        if (opts.onProgress) { try { opts.onProgress(Math.max(0, Math.min(1, elapsed / len))); } catch { /* noop */ } }
        if (video.currentTime >= start + len - 0.05 || video.ended) { resolve(); return; }
        if (drawFrame && typeof video.requestVideoFrameCallback === "function") rvfc = video.requestVideoFrameCallback(tick);
        else raf = requestAnimationFrame(tick);
      };
      tick();
    });

    try { recorder.stop(); } catch { /* noop */ }
    await stopped;
    if (opts.onProgress) { try { opts.onProgress(1); } catch { /* noop */ } }

    const outType = (mimeType.split(";")[0]) || "video/webm";
    const ext = outType.includes("mp4") ? "mp4" : "webm";
    const blob = new Blob(chunks, { type: outType });
    if (!blob.size) throw new Error("empty-clip");
    const base = (file.name || "clip").replace(/\.[^.]+$/, "").slice(0, 40) || "clip";
    return new File([blob], `${base}-clip.${ext}`, { type: outType });
  } finally {
    cleanup();
  }
};
